import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';

function Customers() {
  document.title = 'Customers';

  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    quickbooks_id: '',
    email: '',
    phone: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_contact_name: '',
    billing_contact_phone: '',
    billing_contact_emails: [],
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_contact_name: '',
    shipping_contact_phone: '',
    shipping_contact_email: '',
    tax_id: '',
    payment_terms: '',
    notes: '',
  });
  const [newBillingEmail, setNewBillingEmail] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .order('nickname', { ascending: true, nullsFirst: false });
        if (error) throw error;
        setCustomers(data || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
        toast.error(`Failed to fetch customers: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [user]);

  const openModal = (mode, customer = null) => {
    setModalMode(mode);
    setNewBillingEmail('');
    if (mode === 'edit' && customer) {
      setSelectedCustomer(customer);
      let billingEmails = [];
      if (customer.billing_contact_emails && Array.isArray(customer.billing_contact_emails)) {
        billingEmails = customer.billing_contact_emails;
      } else if (customer.billing_contact_email) {
        billingEmails = [customer.billing_contact_email];
      }
      setFormData({
        name: customer.name || '',
        nickname: customer.nickname || '',
        quickbooks_id: customer.quickbooks_id || '',
        email: customer.email || '',
        phone: customer.phone || '',
        billing_address: customer.billing_address || '',
        billing_city: customer.billing_city || '',
        billing_state: customer.billing_state || '',
        billing_zip: customer.billing_zip || '',
        billing_contact_name: customer.billing_contact_name || '',
        billing_contact_phone: customer.billing_contact_phone || '',
        billing_contact_emails: billingEmails,
        shipping_address: customer.shipping_address || '',
        shipping_city: customer.shipping_city || '',
        shipping_state: customer.shipping_state || '',
        shipping_zip: customer.shipping_zip || '',
        shipping_contact_name: customer.shipping_contact_name || '',
        shipping_contact_phone: customer.shipping_contact_phone || '',
        shipping_contact_email: customer.shipping_contact_email || '',
        tax_id: customer.tax_id || '',
        payment_terms: customer.payment_terms || '',
        notes: customer.notes || '',
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        name: '',
        nickname: '',
        quickbooks_id: '',
        email: '',
        phone: '',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_zip: '',
        billing_contact_name: '',
        billing_contact_phone: '',
        billing_contact_emails: [],
        shipping_address: '',
        shipping_city: '',
        shipping_state: '',
        shipping_zip: '',
        shipping_contact_name: '',
        shipping_contact_phone: '',
        shipping_contact_email: '',
        tax_id: '',
        payment_terms: '',
        notes: '',
      });
    }
    setError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddBillingEmail = () => {
    const email = newBillingEmail.trim().toLowerCase();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.billing_contact_emails.includes(email)) {
      toast.error('This email is already added');
      return;
    }

    setFormData(prev => ({
      ...prev,
      billing_contact_emails: [...prev.billing_contact_emails, email],
    }));
    setNewBillingEmail('');
  };

  const handleRemoveBillingEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      billing_contact_emails: prev.billing_contact_emails.filter(e => e !== emailToRemove),
    }));
  };

  const handleBillingEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddBillingEmail();
    }
  };

  const handleSaveCustomer = async () => {
    if (!formData.name) {
      setError('Customer name is required.');
      return;
    }

    try {
      const saveData = {
        ...formData,
        billing_contact_email: formData.billing_contact_emails.length > 0
          ? formData.billing_contact_emails[0]
          : null,
      };

      if (modalMode === 'create') {
        const { error } = await supabase
          .from('customers')
          .insert(saveData);
        if (error) throw error;
        toast.success('Customer created successfully!');
      } else {
        const { error } = await supabase
          .from('customers')
          .update(saveData)
          .eq('id', selectedCustomer.id);
        if (error) throw error;
        toast.success('Customer updated successfully!');
      }

      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('*')
        .order('nickname', { ascending: true, nullsFirst: false });
      if (fetchError) throw fetchError;
      setCustomers(data || []);

      closeModal();
    } catch (err) {
      console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} customer:`, err);
      setError(`Failed to ${modalMode === 'create' ? 'create' : 'update'} customer: ${err.message}`);
      toast.error(`Failed to ${modalMode === 'create' ? 'create' : 'update'} customer: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="h-12 px-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-slate-800">Customers</h1>
          <button
            onClick={() => openModal('create')}
            className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
          >
            + Add Customer
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="border-b border-slate-200">
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Nickname</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Full Name</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Phone</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Billing Emails</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Terms</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {customers.map((customer) => (
              <tr
                key={customer.id}
                className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
              >
                <td className="px-4 py-2">
                  <button
                    onClick={() => openModal('edit', customer)}
                    className="text-sm font-medium text-slate-800 hover:text-slate-600 hover:underline text-left"
                  >
                    {customer.nickname || customer.name}
                  </button>
                </td>
                <td className="px-4 py-2 text-sm text-slate-600">{customer.name}</td>
                <td className="px-4 py-2 text-sm text-slate-600">{customer.phone || '-'}</td>
                <td className="px-4 py-2 text-sm text-slate-600">
                  {customer.billing_contact_emails?.length > 0
                    ? customer.billing_contact_emails.join(', ')
                    : customer.billing_contact_email || '-'}
                </td>
                <td className="px-4 py-2 text-sm text-slate-600">{customer.payment_terms || '-'}</td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-sm text-slate-500">
                  No customers found. Click "+ Add Customer" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Customer Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                  <div className="border-b border-slate-200 px-4 py-3">
                    <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">
                      {modalMode === 'create' ? 'Add Customer' : 'Edit Customer'}
                    </Dialog.Title>
                  </div>

                  <div className="p-4 max-h-[70vh] overflow-y-auto">
                    {error && <p className="mb-4 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm">{error}</p>}

                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Nickname</label>
                        <input
                          type="text"
                          name="nickname"
                          value={formData.nickname}
                          onChange={handleInputChange}
                          placeholder="e.g., JBT Kingston"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">QuickBooks ID</label>
                        <input
                          type="text"
                          name="quickbooks_id"
                          value={formData.quickbooks_id}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Payment Terms</label>
                        <input
                          type="text"
                          name="payment_terms"
                          value={formData.payment_terms}
                          onChange={handleInputChange}
                          placeholder="e.g., Net 30"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                    </div>

                    {/* Billing Information */}
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 mt-4">Billing Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-2">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">Billing Address</label>
                        <input
                          type="text"
                          name="billing_address"
                          value={formData.billing_address}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
                        <input
                          type="text"
                          name="billing_city"
                          value={formData.billing_city}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="w-20">
                          <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
                          <input
                            type="text"
                            name="billing_state"
                            value={formData.billing_state}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-slate-600 mb-1">ZIP</label>
                          <input
                            type="text"
                            name="billing_zip"
                            value={formData.billing_zip}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Contact Name</label>
                        <input
                          type="text"
                          name="billing_contact_name"
                          value={formData.billing_contact_name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Contact Phone</label>
                        <input
                          type="text"
                          name="billing_contact_phone"
                          value={formData.billing_contact_phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Tax ID</label>
                        <input
                          type="text"
                          name="tax_id"
                          value={formData.tax_id}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Billing Emails</label>
                      {formData.billing_contact_emails.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {formData.billing_contact_emails.map((email, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-sm"
                            >
                              <span>{email}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveBillingEmail(email)}
                                className="text-slate-500 hover:text-slate-700 ml-1"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={newBillingEmail}
                          onChange={(e) => setNewBillingEmail(e.target.value)}
                          onKeyDown={handleBillingEmailKeyDown}
                          placeholder="Enter email address"
                          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                        <button
                          type="button"
                          onClick={handleAddBillingEmail}
                          className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Shipping Information */}
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 mt-4">Shipping Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-2">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">Shipping Address</label>
                        <input
                          type="text"
                          name="shipping_address"
                          value={formData.shipping_address}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
                        <input
                          type="text"
                          name="shipping_city"
                          value={formData.shipping_city}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="w-20">
                          <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
                          <input
                            type="text"
                            name="shipping_state"
                            value={formData.shipping_state}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-slate-600 mb-1">ZIP</label>
                          <input
                            type="text"
                            name="shipping_zip"
                            value={formData.shipping_zip}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Contact Name</label>
                        <input
                          type="text"
                          name="shipping_contact_name"
                          value={formData.shipping_contact_name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Contact Phone</label>
                        <input
                          type="text"
                          name="shipping_contact_phone"
                          value={formData.shipping_contact_phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Contact Email</label>
                        <input
                          type="email"
                          name="shipping_contact_email"
                          value={formData.shipping_contact_email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveCustomer}
                      className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                    >
                      {modalMode === 'create' ? 'Add Customer' : 'Save Changes'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default Customers;
